"use client";
import { WEB_COLORS } from "@/lib/constants";
import { useGoogleMapsScript, useGooglePlacesAutocomplete, usePlaceGeocode } from "@/lib/hooks/googleapi.hooks";
import { useGetAutoSuggestionMapplsQuery, useLazyGetCoordinatesMapplsQuery, useLazyGetReverseGeocodeMapplsQuery } from "@/lib/redux/apis/map-my-india";
import { LocationOn } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  debounce,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

export default function MapplsAutosuggestion({ onSelect, label= "Search", onError }) {
  const [query, setQuery] = useState(null);
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [loader, setLoader] = useState(false); 
  const isSelecting = useRef(false);
  const onSelectRef = useRef(onSelect);
  const onErrorRef = useRef(onError);
  onSelectRef.current = onSelect;
  onErrorRef.current = onError;

  useEffect(() => {
    if (isSelecting.current) {
      isSelecting.current = false;
      return;
    }
    const debouncedSetQuery = debounce(() => {
      setQuery(inputValue);
    }, 500);
    debouncedSetQuery();
    return () => debouncedSetQuery.clear?.();
  }, [inputValue]);



//  const loaded = useGoogleMapsScript();
//  const googleoptions = useGooglePlacesAutocomplete(inputValue);
//  const { getCoordinates: getGoogleCoordinates } = usePlaceGeocode();

  const { data, isLoading, error } = useGetAutoSuggestionMapplsQuery(query, {
    skip: !query || query.length < 3,
  });

  const [
    getCoordinates,
    {
      data: coordinatesData,
      isLoading: coordinatesLoading,
      error: coordinatesError,
      reset: resetCoordinates,
    },
  ] = useLazyGetCoordinatesMapplsQuery();

  const [
    getReverseGeocoded,
    {
      data: reverseGeocodedData,
      isLoading: reverseGeocodedLoading,
      error: reverseGeocodedError,
      reset: resetReverseGeocoded,
    },
  ] = useLazyGetReverseGeocodeMapplsQuery();

  useEffect(() => {
    if (error || coordinatesError || reverseGeocodedError) {
      onErrorRef.current?.(error || coordinatesError || reverseGeocodedError);
    }
  }, [error, coordinatesError, reverseGeocodedError]);

  const handleOptionChange = async (_event, newValue) => {
    try {
      console.log("Selected option:", newValue);
      isSelecting.current = true;
      setValue(newValue);
      setInputValue(newValue?.placeAddress || "");
      setLoader(true);
      const res = await getCoordinates(newValue?.eLoc)?.unwrap();
      const reverseGeocodeRes = await getReverseGeocoded({
        lat: res?.latitude,
        lng: res?.longitude,
      })?.unwrap();
      const address = reverseGeocodeRes?.results?.[0];

      const {
        city,
        state,
        area: country,
        district,
        pincode: pin,
      } = address || {};

      const coordinates = {
        latitude: res?.latitude,
        longitude: res?.longitude,
      };
      onSelectRef.current?.({
        ...newValue,
        ...coordinates,
        city: city || district,
        state,
        country,
        pin,
      });
      setQuery(null);
    } catch (err) {
      onErrorRef.current?.(err);
    } finally {
      resetCoordinates();
      setLoader(false);
    }
  };

  const options = useMemo(() => {
    const list = data?.suggestedLocations || [];
    return list.map((item) => ({
    //   label: item.placeAddress,
    //   value: item.placeAddress,
      eLoc: item.eLoc,
      order: item.orderIndex,
      placeName: item.placeName,
      placeAddress: item.placeAddress,
      alternateName: item.alternateName,
    }));
  }, [data]);

  return (
    <Box>
      <Autocomplete
        id="mappls-autosuggestion"
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.placeAddress || ""
        }
        filterOptions={(x) => x}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
          },
        }}
        value={value}
        inputValue={inputValue}
        options={options}
        autoComplete
        loading={isLoading}
        noOptionsText="No locations"
        onChange={handleOptionChange}
        onInputChange={(_event, newInputValue, reason) => {
          console.log("Input changed:", newInputValue, reason);
          if (reason === "input") {
            setInputValue(newInputValue);
          }
        }}
        isOptionEqualToValue={(option, val) => option.eLoc === val?.eLoc}
        renderInput={(params) => (
          <TextField required {...params} label={label} fullWidth />
        )}
        renderOption={(props, option) => {
          const { placeAddress, placeName, order } = option || {};
          return (
            <li {...props} key={order}>
              <Grid container sx={{ alignItems: "center" }}>
                <Grid item sx={{ display: "flex", width: 44 }}>
                  <LocationOn sx={{ color: WEB_COLORS.primary }} />
                </Grid>
                <Grid
                  item
                  sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                  {placeName && (
                    <Typography variant="body2" color="text.secondary">
                      {placeName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {placeAddress}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
      {/* {query?.length < 3 && (
        <Typography fontSize={14} color={WEB_COLORS.errorColor}>
          Minimum 3 character required
        </Typography>
      )} */}
    </Box>
  );
}
